// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {Link as RouterLink, matchRoutes, NavLink as RouterNavLink, resolvePath} from 'react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {type ReactElement, use} from 'react'
import {PREVENT_AUTOFOCUS_KEY, type PreventAutofocusProp} from '../shared'
import {RoutesContext} from './use-navigate'

/**
 * React Router Link that supports preventAutofocus and resolves
 * reloadDocument based on whether the target route is within the app's RoutesContext.
 */
export const ExtendedLink = ({
  to,
  reloadDocument,
  preventAutofocus,
  ...props
}: React.ComponentProps<typeof RouterLink> & PreventAutofocusProp): ReactElement => {
  const {routes} = use(RoutesContext)
  const pathname = resolvePath(to, ssrSafeLocation.pathname).pathname
  reloadDocument = reloadDocument ?? !matchRoutes(routes, pathname)
  return (
    <RouterLink
      to={to}
      {...props}
      state={
        preventAutofocus
          ? {
              [PREVENT_AUTOFOCUS_KEY]: true,
              ...props.state,
            }
          : props.state
      }
      reloadDocument={reloadDocument}
    />
  )
}

/**
 * React Router NavLink that supports preventAutofocus and resolves
 * reloadDocument based on whether the target route is within the app's RoutesContext.
 */
export function ExtendedNavLink({
  to,
  reloadDocument,
  preventAutofocus,
  ...props
}: React.ComponentProps<typeof RouterNavLink> & PreventAutofocusProp): React.ReactElement {
  const {routes} = use(RoutesContext)
  const pathname = resolvePath(to, ssrSafeLocation.pathname).pathname
  reloadDocument = reloadDocument ?? !matchRoutes(routes, pathname)
  return (
    <RouterNavLink
      to={to}
      {...props}
      state={
        preventAutofocus
          ? {
              [PREVENT_AUTOFOCUS_KEY]: true,
              ...props.state,
            }
          : props.state
      }
      reloadDocument={reloadDocument}
    />
  )
}

// Re-export non-wrapped versions of Link and NavLink for use in cases where external
// route detection is not supported (e.g., Memex)
export {RouterNavLink as NavLink, RouterLink as Link}
